import type { HTMLAttributes, ReactNode } from 'react'
import { useEffect } from 'react'
import { cn } from '../lib/cn'
import { Button } from './button'

export type DialogProps = {
  open: boolean
  title: string
  description?: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  className?: string
}

export function Dialog({
  open,
  title,
  description,
  onClose,
  children,
  footer,
  className,
}: DialogProps) {
  useEffect(() => {
    if (!open) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [open])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <button
        type="button"
        className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
        aria-label="Close dialog"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        className={cn(
          'relative z-10 flex max-h-[min(90dvh,calc(100vh-2rem))] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-border-default bg-surface-card shadow-2xl',
          className,
        )}
      >
        <div className="shrink-0 border-b border-border-default px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 id="dialog-title" className="text-lg font-semibold text-text-primary">
                {title}
              </h2>
              {description ? (
                <p className="mt-1 text-sm text-text-muted">{description}</p>
              ) : null}
            </div>
            <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Close">
              ×
            </Button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">
          {children}
        </div>
        {footer ? (
          <div className="flex shrink-0 justify-end gap-2 border-t border-border-default bg-surface-card px-5 py-4">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export function DialogFooter({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('flex justify-end gap-2', className)} {...props}>
      {children}
    </div>
  )
}
